/*
 * Copyright (C) 2025 JPMorgan Chase & Company
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, is not permitted.
 */

import {ExdsButton, ExdsGrid, ExdsLayout, ExdsText} from "@exds/core";
import React, {useContext, useEffect} from "react";
import './RsaDownloadApp.scss';
import AppContext from "../../../../AppContext";
import ButtonGroup from "../../../ButtonGroup/ButtonGroup";
import RsaHeader from "../RsaHeader/RsaHeader";
import appleStoreBadge from '../../../../assets/images/Apple_AppStore_Badge.png';
import googlePlayBadge from '../../../../assets/images/Android_GooglePlay_Badge.png';
import QRCode from 'react-qr-code';
import {useNavigate} from "react-router-dom";
import {addPageLoadEvent} from "../../../AdobeAnalytics/AdobeAnalytics";

export default function RsaDownloadApp(props) {
    const {setActiveMobileAppSetupPage, setPageName} = props;
    const {mobileDeviceContext, loadAA, sidContext} = useContext(AppContext);
    const [mobileDevice, setMobileDevice] = mobileDeviceContext;
    const navigate = useNavigate();
    const sid = sidContext.at(0);

  useEffect(() => {
    if(loadAA) {
      const pageName = 'Mobile App Setup | RSA Download App';
      setPageName(pageName);
      addPageLoadEvent(pageName, sid);
    }
  }, [loadAA, sid]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancelModalDetails = {
    title: 'Are you sure you want to cancel Mobile App Setup?',
    modalActionButton: 'Yes, I want to cancel',
    continueButton: 'No, let\'s continue'
  };

  const deviceContent = {
    'IPHONE': {
      badgeSrc: appleStoreBadge,
      qrCodeSrc: 'https://apps.apple.com/us/app/rsa-authenticator-securid/id318038618',
      linkText: 'Switch to Android',
      storeName: 'Apple Store',
      paragraphStoreName: 'the Apple Store',
      credentials: 'Apple ID'
    },
    'ANDROID': {
      badgeSrc: googlePlayBadge,
      qrCodeSrc: 'https://play.google.com/store/apps/details?id=com.rsa.securidapp',
      linkText: 'Switch to Apple iOS',
      storeName: 'Google Play',
      paragraphStoreName: 'Google Play',
      credentials: 'Google'
    }
  };

    return (
        <ExdsGrid columnSpan={8} className='step-details'>
            <RsaHeader />
            <ExdsText primitive="div" textStyle="bodyMediumHeavier" className="rsa-step">
                Step 1 of 3
                <br />
                <span className="rsa-step-title">Download RSA Authenticator to your mobile device</span>
            </ExdsText>
            <ExdsLayout flexDirection="row" gap="2rem" alignItems="flex-start" className="download-content-container">
                <ExdsLayout flexDirection="column" className="app-download">
                    <img className="os-badge" src={deviceContent[mobileDevice].badgeSrc} alt={`${deviceContent[mobileDevice].storeName} Badge`} />
                    <QRCode className="mt-1 qr-code" value={deviceContent[mobileDevice].qrCodeSrc}
                            alt={`${deviceContent[mobileDevice].storeName} QR Code`}
                            data-testid="testid-qrcode-image" />
                    <ExdsButton
                        text={deviceContent[mobileDevice].linkText}
                        variant="link"
                        className="os-link"
                        onClick={() => setMobileDevice(mobileDevice === 'IPHONE' ? 'ANDROID' : 'IPHONE')}
                        type="button"
                    />
                </ExdsLayout>
                <ExdsLayout flexDirection="column">
                    <ExdsText primitive="div" textStyle="bodyLarge">
                        Using your mobile device's camera, scan the QR code to download and install the &nbsp;
                        <b>RSA Authenticator (SecurID)</b> from {deviceContent[mobileDevice].paragraphStoreName}.
                        You may need to enter your {deviceContent[mobileDevice].credentials} credentials to download.
                        <br />
                        <br />
                        Once you've downloaded and installed the app to your phone, click continue.
                    </ExdsText>
                </ExdsLayout>
            </ExdsLayout>
            <ButtonGroup
                advanceButtonContent='Continue'
                advanceButtonAction={() => setActiveMobileAppSetupPage('createPin')}
                modalButtonContent='Cancel'
                modalButtonType='button'
                modalDetails={cancelModalDetails}
                modalAction={ () => navigate('/finish')}
                backButtonContent='Back'
                backButtonAction={() => setActiveMobileAppSetupPage('rsaInfo')}
            />
        </ExdsGrid>
    );
};
